import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail, formatMoney2, formatPct } from "../lib/api";
import { BannerFarmanaliza } from "../components/BannerFarmanaliza";
import { ExportButtons } from "../lib/exports";
import { IncentivoStockD } from "../components/empleados/IncentivoStockD";
import { TramosEditor } from "../components/empleados/TramosEditor";
import { Loader2, Target, Save, Wand2, ArrowLeft, Trophy } from "lucide-react";

function AchievementBar({ value, cobra }) {
  const pct = Math.min(value * 100, 130) / 1.3;
  const color = cobra ? "bg-emerald-500" : value >= 1 ? "bg-amber-400" : "bg-red-400";
  return (
    <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
      <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} />
    </div>
  );
}

export default function Incentivos({ embedded = false }) {
  const [imports, setImports] = useState([]);
  const [importId, setImportId] = useState(null);
  const [data, setData] = useState(null);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data: list } = await api.get("/sales/imports");
        setImports(list);
        const withTeam = list.find((i) => (i.employees || []).some((e) => e !== "SIN ASIGNAR"));
        setImportId((withTeam || list[0])?.id || null);
      } catch (e) {
        setErr(formatApiErrorDetail(e.response?.data?.detail) || "No se pudieron cargar las subidas");
      } finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    if (!importId) { setData(null); return; }
    let ignore = false;
    (async () => {
      try {
        const { data: d } = await api.get(`/sales/incentives/${importId}`);
        if (!ignore) { setData(d); setPlan(d.plan); setErr(""); }
      } catch (e) {
        if (!ignore) setErr(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo calcular el plan de incentivos");
      }
    })();
    return () => { ignore = true; };
  }, [importId]);

  const recompute = async (nextPlan) => {
    setPlan(nextPlan);
    setSaving(true); setErr(""); setMsg("");
    try {
      await api.put("/sales/incentive-plan", nextPlan);
      const { data: d } = await api.get(`/sales/incentives/${importId}`);
      setData(d); setPlan(d.plan);
      setMsg("Plan guardado y recalculado");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo guardar el plan");
    } finally { setSaving(false); }
  };

  const propose = async () => {
    if (!importId) return;
    setSaving(true); setErr(""); setMsg("");
    try {
      await api.post(`/sales/incentive-plan/propose?import_id=${importId}`);
      const { data: d } = await api.get(`/sales/incentives/${importId}`);
      setData(d); setPlan(d.plan);
      setMsg("Objetivos automáticos: mismo mes del año anterior y mes anterior");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "No se pudieron proponer los objetivos");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-8 flex gap-2 text-slate-500"><Loader2 className="animate-spin" size={18} /> Cargando…</div>;

  if (!imports.length) {
    return (
      <div className="p-8" data-testid="incentivos-empty">
        {!embedded && <Link to="/ventas" className="btn btn-outline mb-6"><ArrowLeft size={15} /> Ventas</Link>}
        <div className="card p-10 text-center text-slate-500">
          <Target size={32} className="mx-auto text-slate-300" />
          <p className="mt-3 font-semibold">Necesitas subir las ventas del mes</p>
          <p className="text-sm">Los incentivos se calculan sobre el diario de ventas con empleado.</p>
        </div>
      </div>
    );
  }

  const metrics = data?.metrics || [];
  const results = data?.results || [];
  const cols = [
    { header: "Empleado", key: "vendedor" },
    { header: "Cobro de la bolsa (%)", key: "score", format: (v) => ((v || 0) * 100).toFixed(1), align: "right" },
    { header: "Incentivo (€)", key: "bonus", format: (v) => (v || 0).toFixed(2), align: "right" },
  ];

  return (
    <div className={embedded ? "fade-in" : "p-8 fade-in"} data-testid="incentivos-page">
      {!embedded && <BannerFarmanaliza compact image="/img/farmanaliza-incentivos.jpg?v=2" kicker="Incentivos" subtitle="Un equipo motivado vende más"
                                       text="Incentivos individuales, de grupo y de stock D para premiar a tu equipo." testid="banner-incentivos" />}
      {!embedded && <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Indicadores comerciales</div>
          <h1 className="font-display text-4xl font-extrabold mt-1 tracking-tight">Plan de incentivos</h1>
          <p className="text-slate-600 mt-1 text-sm">Objetivos mensuales por empleado y bono calculado sobre el cumplimiento real.</p>
        </div>
        <Link to="/ventas" className="btn btn-outline shrink-0 whitespace-nowrap" data-testid="back-to-ventas"><ArrowLeft size={15} /> Ventas</Link>
      </div>}

      <div className="flex items-center gap-2 mb-6 flex-wrap">
        {imports.map((im) => (
          <button key={im.id} onClick={() => setImportId(im.id)}
                  className={"px-3 py-2 rounded-lg text-sm font-semibold border transition " +
                    (importId === im.id ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-slate-400")}
                  data-testid={`inc-import-tab-${im.id}`}>
            {im.label}
          </button>
        ))}
      </div>

      {err && <div className="card mb-6 border border-red-300 bg-red-50 px-5 py-3 text-sm text-red-700" data-testid="incentivos-error">{err}</div>}
      {!data && !err && <div className="flex gap-2 text-slate-500 py-6" data-testid="incentivos-loading"><Loader2 className="animate-spin" size={18} /> Calculando incentivos…</div>}

      {data?.proposed && (
        <div className="card mb-6 border-2 border-amber-400 bg-amber-50/60 px-5 py-3 text-sm text-amber-900" data-testid="proposed-banner">
          <b>Propuesta automática:</b> cada objetivo es lo que hizo esa persona el mismo mes del año anterior (venta libre y consejo) o el mes anterior (ticket medio, margen y stock parado). Pulsa <b>Guardar plan</b> para fijar pesos y bolsa.
        </div>
      )}

      {plan && (
        <div className="card p-6 mb-6" data-testid="plan-editor">
          <h3 className="font-display text-xl font-bold mb-4">Reglas del plan</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
            <div>
              <label className="text-xs font-semibold text-slate-600">Bolsa de incentivo por empleado (€)</label>
              <input type="number" min="0" step="10" className="input w-full mt-1" value={plan.bonus_pool_per_employee}
                     onChange={(e) => setPlan({ ...plan, bonus_pool_per_employee: Number(e.target.value) })}
                     data-testid="bonus-pool-input" />
            </div>
            <div className="md:col-span-1 text-xs text-slate-600" data-testid="tramos-info">
              <div className="font-semibold text-slate-700 mb-1">Solo se cobra si se supera el objetivo</div>
              <div>{[...(plan.tramos || [])].sort((a, b) => a.supera - b.supera).map((t) => `+${t.supera} % → ${t.cobro} %`).join(" · ")}</div>
            </div>
            <div className="flex items-end gap-2">
              <button onClick={() => recompute(plan)} disabled={saving} className="btn btn-primary" data-testid="save-plan">
                {saving ? <><Loader2 size={14} className="animate-spin" /> Guardando…</> : <><Save size={14} /> Guardar plan</>}
              </button>
              <button onClick={propose} disabled={saving} className="btn btn-outline" data-testid="propose-targets">
                <Wand2 size={14} /> Volver a objetivos automáticos
              </button>
            </div>
          </div>

          <TramosEditor tramos={plan.tramos} onChange={(tramos) => setPlan({ ...plan, tramos })} />

          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Peso de cada indicador (%)</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
            {metrics.map((m) => (
              <div key={m.key}>
                <label className="text-xs text-slate-600">{m.label}</label>
                <input type="number" min="0" max="100" className="input w-full mt-1" value={plan.weights?.[m.key] ?? 0}
                       onChange={(e) => setPlan({ ...plan, weights: { ...plan.weights, [m.key]: Number(e.target.value) } })}
                       data-testid={`weight-${m.key}`} />
              </div>
            ))}
          </div>

          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Objetivos mensuales por empleado</div>
          <div className="mb-3 text-xs rounded-lg border px-3 py-2 bg-slate-50 border-slate-200 text-slate-700" data-testid="targets-mode">
            {plan.modo_objetivos === "manual" ? (
              <><b>Objetivos manuales:</b> se usan las cifras que has escrito. Pulsa «Volver a objetivos automáticos» para calcularlos otra vez por histórico.</>
            ) : (
              <><b>Objetivos automáticos:</b> venta libre, consejo y consejo en receta = lo que hizo cada persona el <b>mismo mes del año anterior</b>{data?.referencias?.yoy ? ` (${data.referencias.yoy})` : " (no hay datos de ese mes)"}; ticket medio, margen y stock parado = su <b>mes anterior</b>{data?.referencias?.mom ? ` (${data.referencias.mom})` : " (no hay datos de ese mes)"}. Si falta el dato se usa la media del equipo (en ámbar). Si cambias una cifra, el plan pasa a manual.</>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="dense" data-testid="targets-table">
              <thead>
                <tr><th>Empleado</th>{metrics.map((m) => <th key={m.key} className="num">{m.label}</th>)}</tr>
              </thead>
              <tbody>
                {Object.keys(plan.targets || {}).sort().map((emp) => (
                  <tr key={emp}>
                    <td className="font-semibold">{emp}</td>
                    {metrics.map((m) => (
                      <td key={m.key} className="num">
                        <input type="number" step="1" className="input text-xs w-24 text-right"
                               value={plan.targets[emp]?.[m.key] ?? 0}
                               onChange={(e) => setPlan({
                                 ...plan, modo_objetivos: "manual",
                                 targets: { ...plan.targets, [emp]: { ...plan.targets[emp], [m.key]: Number(e.target.value) } },
                               })}
                               data-testid={`target-${emp}-${m.key}`} />
                        {plan.modo_objetivos !== "manual" && data?.target_sources?.[emp]?.[m.key] && (
                          <div className={"text-[10px] mt-0.5 " + (data.target_sources[emp][m.key] === "equipo" ? "text-amber-600" : "text-slate-400")}>
                            {{ yoy: "mismo mes año ant.", mom: "mes anterior", equipo: "media equipo" }[data.target_sources[emp][m.key]]}
                          </div>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {msg && <div className="mt-3 text-xs text-emerald-700" data-testid="plan-msg">{msg}</div>}
        </div>
      )}

      <div className="card p-6" data-testid="incentives-results">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500 font-semibold"><Trophy size={12} /> Resultado</div>
            <h3 className="font-display text-xl font-bold">Incentivo por empleado</h3>
          </div>
          <ExportButtons filename="incentivos" title="Plan de incentivos" sheetName="Incentivos"
                         columns={cols} rows={results} testIdPrefix="export-incentivos" />
        </div>
        <div className="space-y-4">
          {results.map((r) => (
            <div key={r.vendedor} className="border border-slate-200 rounded-xl p-4" data-testid={`incentive-${r.vendedor}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="font-display font-bold text-lg">{r.vendedor}</div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Incentivo</div>
                  <div className="font-display text-2xl font-extrabold text-emerald-600" data-testid={`bonus-${r.vendedor}`}>{formatMoney2(r.bonus)}</div>
                  <div className="text-xs text-slate-500">cobra el {formatPct(r.score)} de la bolsa</div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                {r.detail.map((d) => (
                  <div key={d.key}>
                    <div className="text-[11px] text-slate-500 truncate" title={d.label}>{d.label}</div>
                    <div className="text-sm font-semibold">{formatMoney2(d.actual)} <span className="text-slate-400 font-normal">/ {formatMoney2(d.target)}</span></div>
                    <AchievementBar value={d.achievement} cobra={d.cobro > 0} />
                    <div className="text-[11px] text-slate-500 mt-0.5">{formatPct(d.achievement)} · <span className={d.cobro > 0 ? "text-emerald-700 font-semibold" : ""}>{d.cobro > 0 ? `cobra ${Math.round(d.cobro * 100)} %` : "no cobra"}</span> · {formatMoney2(d.bonus)}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {results.length === 0 && data && (
            <div className="text-center text-slate-500 py-8" data-testid="no-employees">
              Esta subida no trae empleados. Sube el diario de ventas de Farmatic o el listado de Nixfarma.
            </div>
          )}
        </div>
      </div>
      {importId && <IncentivoStockD importId={importId} />}
    </div>
  );
}
